/* =====================================================================
 * WidgetPage — 3D Tilt + Fluid Glow Dock + 플로팅 캔버스
 *
 * - 글래스모피즘 스타일의 Dock 바
 * - 3D perspective tilt + fluid glow 기반 호버 효과
 * - 캔버스 위 다중 플로팅 윈도우 (메모 등)
 * - 드래그·리사이즈·핀 고정·z-index 관리
 * ===================================================================== */

import React, { useState, useCallback, Suspense, lazy, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import {
  MessageSquare,
  Calendar,
  Mail,
  StickyNote,
  Hand,
  type LucideIcon,
} from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useUserStore } from '@auw/stores';
import { getActiveAccountId } from '@auw/api-client';
import { activeTenantNamespacedKey as tenantNamespacedKey } from '@auw/tenant';
import { EmptyState } from '@auw/workspace-ui';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { useFeatureFlags, type WidgetSubFeatureId } from '@auw/feature-flags';
import { emitAppEvent } from '@auw/stores';
import type { MailProvider, CalendarProviderId } from '@auw/api-client';
import type { FileAttachment } from '@auw/workspace-utils';
import { MemoWindow } from './MemoWindow';
import type { MemoData, FloatingRect } from './MemoWindow';
import {
  deleteMemoImageAssets,
  extractMemoImageIds,
  loadMemoImageAssets,
  loadPersistedWidgetSnapshot,
  pruneOrphanMemoImageAssets,
  savePersistedWidgetWindows,
  upsertMemoImageAsset,
  type PersistedMemoWindow,
} from './widgetPersistence';
import {
  fetchWidgetCanvasFromCloud,
  saveWidgetCanvasToCloud,
} from '@auw/api-client';
import { useTranslation } from 'react-i18next';

const EmailModal = lazy(() =>
  import('../chat/EmailModal').then((m) => ({ default: m.EmailModal })),
);

const DMWidgetModal = lazy(() =>
  import('../dm/DMWidgetModal').then((m) => ({ default: m.DMWidgetModal })),
);

const AgentWidgetModal = lazy(() =>
  import('../chat/AgentWidgetModal').then((m) => ({ default: m.AgentWidgetModal })),
);

const CalendarWidgetModal = lazy(() =>
  import('./CalendarWidgetModal').then((m) => ({ default: m.CalendarWidgetModal })),
);

/* ------------------------------------------------------------------ */
/* Dock Config                                                         */
/* ------------------------------------------------------------------ */

const ICON_SIZE = 44;
const ICON_GAP = 14;
const DOCK_PAD_X = 16;
const DOCK_PAD_Y = 8;
const ICON_RADIUS = '22.37%';
const LIFT_Y = -14;
const TILT_DEG = 8;
const NEIGHBOR_TILT_DEG = 4;
const WIDGET_STAGE_MAX_W = 1920;
const WIDGET_STAGE_MOBILE_BREAKPOINT = 768;

interface DockItemConfig {
  id: string;
  label: string;
  icon?: LucideIcon;
  iconSrc?: string;
  iconContain?: boolean;
}

const DOCK_APPS: DockItemConfig[] = [
  { id: 'agent', label: 'dock.agent', iconSrc: '/agent_logo.png', iconContain: true },
  { id: 'chat', label: 'dock.chat', icon: MessageSquare },
  { id: 'calendar', label: 'dock.calendar', icon: Calendar },
  { id: 'mail', label: 'dock.mail', icon: Mail },
  { id: 'memo', label: 'dock.memo', icon: StickyNote },
];

/* Phase 11-5: each dock entry maps 1:1 to a `mode.widget.dock.*`
 * tenant flag.  Disabling a flag drops the dock icon entirely; the
 * `pan` tool is always present because it gates canvas navigation,
 * not a user-visible egress surface. */
const DOCK_FEATURE_ID: Record<string, WidgetSubFeatureId> = {
  agent: 'mode.widget.dock.agent',
  chat: 'mode.widget.dock.chat',
  calendar: 'mode.widget.dock.calendar',
  mail: 'mode.widget.dock.mail',
  memo: 'mode.widget.dock.memo',
};

/* ------------------------------------------------------------------ */
/* Floating window state                                               */
/* ------------------------------------------------------------------ */

interface FloatingWindow {
  id: string;
  type: 'memo';
  rect: FloatingRect;
  pinned: boolean;
  opacity: number;
  zIndex: number;
  memo: MemoData;
}

interface CanvasBounds {
  w: number;
  h: number;
}

const MEMO_DEFAULT_W = 340;
const MEMO_DEFAULT_H = 380;
const STAGGER = 28;
const MEMO_MIN_W = 260;
const MEMO_MIN_H = 200;
const CALENDAR_MODAL_DEFAULT_W = 1080;
const CALENDAR_MODAL_DEFAULT_H = 720;
const CALENDAR_MODAL_MIN_W = 560;
const CALENDAR_MODAL_MIN_H = 520;
const EMAIL_MODAL_DEFAULT_W = 860;
const EMAIL_MODAL_DEFAULT_H = 640;
const EMAIL_MODAL_MIN_W = 320;
const EMAIL_MODAL_MIN_H = 360;
const DM_MODAL_DEFAULT_W = 420;
const DM_MODAL_DEFAULT_H = 360;
const DM_MODAL_MIN_W = 420;
const DM_MODAL_MIN_H = 360;
const AGENT_MODAL_DEFAULT_W = 780;
const AGENT_MODAL_DEFAULT_H = 560;
const AGENT_MODAL_MIN_W = 560;
const AGENT_MODAL_MIN_H = 420;
const LEGACY_DM_MODAL_DEFAULT_W = 920;
const LEGACY_DM_MODAL_DEFAULT_H = 660;
const REMOTE_CANVAS_VERSION = 2;
const AGENT_BRAND_SUFFIXES = ['.team', '.agent', '.mate', '.rebo'];
const WIDGET_CALENDAR_MODAL_STATE_KEY = tenantNamespacedKey('widget', 'calendar-modal');
const WIDGET_EMAIL_MODAL_STATE_KEY = tenantNamespacedKey('widget', 'email-modal');
const WIDGET_DM_MODAL_STATE_KEY = tenantNamespacedKey('widget', 'dm-modal');
const WIDGET_AGENT_MODAL_STATE_KEY = tenantNamespacedKey('widget', 'agent-modal');
const WIDGET_LAYOUT_META_STATE_KEY = tenantNamespacedKey('widget', 'layout-meta');

let globalCounter = 0;

function getScopedWidgetModalStateKey(baseKey: string): string {
  const accountId = getActiveAccountId();
  return `${baseKey}:${accountId || 'anonymous'}`;
}

interface PersistedEmailModalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PersistedCalendarModalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PersistedCalendarModalState {
  open: boolean;
  selectedDate: string;
  selectedProviders: CalendarProviderId[];
  rect: PersistedCalendarModalRect;
  pinned: boolean;
  opacity: number;
}

interface PersistedEmailModalState {
  open: boolean;
  initialTab: MailProvider;
  rect: PersistedEmailModalRect;
  pinned: boolean;
  opacity: number;
}

interface PersistedDMModalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PersistedDMModalState {
  open: boolean;
  rect: PersistedDMModalRect;
  pinned: boolean;
  opacity: number;
}

interface PersistedAgentModalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface PersistedAgentModalState {
  open: boolean;
  rect: PersistedAgentModalRect;
  pinned: boolean;
  opacity: number;
}

type WidgetLayoutMode = 'default' | 'custom';

interface WidgetLayoutMeta {
  layoutMode: WidgetLayoutMode;
  templateSourceEmail: string | null;
}

type WidgetModalType = 'calendar' | 'dm' | 'email' | 'agent';

interface WidgetCanvasSnapshot {
  windows: FloatingWindow[];
  calendarModal: PersistedCalendarModalState;
  emailModal: PersistedEmailModalState;
  dmModal: PersistedDMModalState;
  agentModal: PersistedAgentModalState;
}

const DEFAULT_WIDGET_LAYOUT_META: WidgetLayoutMeta = {
  layoutMode: 'custom',
  templateSourceEmail: null,
};

const DEFAULT_WIDGET_MODAL_Z_ORDER: Record<WidgetModalType, number> = {
  calendar: 20,
  dm: 21,
  email: 22,
  agent: 23,
};

function todayDateKey(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function createDefaultCalendarModalState(): PersistedCalendarModalState {
  return {
    open: false,
    selectedDate: todayDateKey(),
    selectedProviders: [],
    rect: { x: 80, y: 0, w: CALENDAR_MODAL_DEFAULT_W, h: CALENDAR_MODAL_DEFAULT_H },
    pinned: false,
    opacity: 1,
  };
}

function normalizeCalendarModalState(
  raw: unknown,
  fallback: PersistedCalendarModalState,
): PersistedCalendarModalState {
  if (!raw || typeof raw !== 'object') {
    return {
      ...fallback,
      selectedProviders: [...fallback.selectedProviders],
      rect: { ...fallback.rect },
    };
  }

  const parsed = raw as Record<string, unknown>;
  const open = typeof parsed.open === 'boolean' ? parsed.open : fallback.open;
  const selectedDateRaw = typeof parsed.selectedDate === 'string'
    ? parsed.selectedDate
    : typeof parsed.selected_date === 'string'
      ? parsed.selected_date
      : fallback.selectedDate;
  const selectedDate = /^\d{4}-\d{2}-\d{2}$/.test(selectedDateRaw)
    ? selectedDateRaw
    : fallback.selectedDate;

  const selectedProvidersRaw = Array.isArray(parsed.selectedProviders)
    ? parsed.selectedProviders
    : Array.isArray(parsed.selected_providers)
      ? parsed.selected_providers
      : fallback.selectedProviders;
  const selectedProviders = selectedProvidersRaw.filter(
    (item): item is CalendarProviderId =>
      item === 'google_calendar' || item === 'outlook_calendar',
  );

  const rectRaw =
    parsed.rect && typeof parsed.rect === 'object'
      ? (parsed.rect as Record<string, unknown>)
      : {};
  const x = Number(rectRaw.x);
  const y = Number(rectRaw.y);
  const w = Number(rectRaw.w);
  const h = Number(rectRaw.h);
  const rect: PersistedCalendarModalRect = {
    x: Number.isFinite(x) ? x : fallback.rect.x,
    y: Number.isFinite(y) ? y : fallback.rect.y,
    w: Number.isFinite(w) ? Math.max(CALENDAR_MODAL_MIN_W, w) : fallback.rect.w,
    h: Number.isFinite(h) ? Math.max(CALENDAR_MODAL_MIN_H, h) : fallback.rect.h,
  };
  const pinned = typeof parsed.pinned === 'boolean' ? parsed.pinned : fallback.pinned;
  const opacityRaw = Number(parsed.opacity);
  const opacity = Number.isFinite(opacityRaw)
    ? Math.min(1, Math.max(0.35, opacityRaw))
    : fallback.opacity;

  return {
    open,
    selectedDate,
    selectedProviders,
    rect,
    pinned,
    opacity,
  };
}

function loadPersistedCalendarModalState(): PersistedCalendarModalState {
  const fallback = createDefaultCalendarModalState();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(getScopedWidgetModalStateKey(WIDGET_CALENDAR_MODAL_STATE_KEY));
    if (!raw) return fallback;
    return normalizeCalendarModalState(JSON.parse(raw), fallback);
  } catch {
    return fallback;
  }
}

function savePersistedCalendarModalState(state: PersistedCalendarModalState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getScopedWidgetModalStateKey(WIDGET_CALENDAR_MODAL_STATE_KEY),
      JSON.stringify(state),
    );
  } catch {
    // 스토리지 접근 불가 시 무시
  }
}

function createDefaultEmailModalState(): PersistedEmailModalState {
  return {
    open: false,
    initialTab: 'gmail',
    rect: { x: 120, y: 0, w: EMAIL_MODAL_DEFAULT_W, h: EMAIL_MODAL_DEFAULT_H },
    pinned: false,
    opacity: 1,
  };
}

function normalizeEmailModalState(
  raw: unknown,
  fallback: PersistedEmailModalState,
): PersistedEmailModalState {
  if (!raw || typeof raw !== 'object') {
    return {
      ...fallback,
      rect: { ...fallback.rect },
    };
  }

  const parsed = raw as Record<string, unknown>;
  const open = typeof parsed.open === 'boolean' ? parsed.open : fallback.open;

  const tabRaw = parsed.initialTab ?? parsed.initial_tab;
  const initialTab: MailProvider =
    tabRaw === 'outlook' || tabRaw === 'gmail' ? tabRaw : fallback.initialTab;

  const rectRaw =
    parsed.rect && typeof parsed.rect === 'object'
      ? (parsed.rect as Record<string, unknown>)
      : {};
  const x = Number(rectRaw.x);
  const y = Number(rectRaw.y);
  const w = Number(rectRaw.w);
  const h = Number(rectRaw.h);
  const rect: PersistedEmailModalRect = {
    x: Number.isFinite(x) ? x : fallback.rect.x,
    y: Number.isFinite(y) ? y : fallback.rect.y,
    w: Number.isFinite(w) ? Math.max(EMAIL_MODAL_MIN_W, w) : fallback.rect.w,
    h: Number.isFinite(h) ? Math.max(EMAIL_MODAL_MIN_H, h) : fallback.rect.h,
  };

  const pinned = typeof parsed.pinned === 'boolean' ? parsed.pinned : fallback.pinned;

  const opacityRaw = Number(parsed.opacity);
  const opacity = Number.isFinite(opacityRaw)
    ? Math.min(1, Math.max(0.35, opacityRaw))
    : fallback.opacity;

  return {
    open,
    initialTab,
    rect,
    pinned,
    opacity,
  };
}

function loadPersistedEmailModalState(): PersistedEmailModalState {
  const fallback = createDefaultEmailModalState();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(getScopedWidgetModalStateKey(WIDGET_EMAIL_MODAL_STATE_KEY));
    if (!raw) return fallback;
    return normalizeEmailModalState(JSON.parse(raw), fallback);
  } catch {
    return fallback;
  }
}

function savePersistedEmailModalState(state: PersistedEmailModalState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getScopedWidgetModalStateKey(WIDGET_EMAIL_MODAL_STATE_KEY),
      JSON.stringify(state),
    );
  } catch {
    // 스토리지 접근 불가 시 무시
  }
}

function createDefaultDMModalState(): PersistedDMModalState {
  return {
    open: false,
    rect: { x: 40, y: 0, w: DM_MODAL_DEFAULT_W, h: DM_MODAL_DEFAULT_H },
    pinned: false,
    opacity: 1,
  };
}

function normalizeDMModalState(
  raw: unknown,
  fallback: PersistedDMModalState,
): PersistedDMModalState {
  if (!raw || typeof raw !== 'object') {
    return {
      ...fallback,
      rect: { ...fallback.rect },
    };
  }

  const parsed = raw as Record<string, unknown>;
  const open = typeof parsed.open === 'boolean' ? parsed.open : fallback.open;
  const rectRaw =
    parsed.rect && typeof parsed.rect === 'object'
      ? (parsed.rect as Record<string, unknown>)
      : {};
  const x = Number(rectRaw.x);
  const y = Number(rectRaw.y);
  const w = Number(rectRaw.w);
  const h = Number(rectRaw.h);
  const isLegacyDefaultRect =
    Number.isFinite(w)
    && Number.isFinite(h)
    && Math.round(w) === LEGACY_DM_MODAL_DEFAULT_W
    && Math.round(h) === LEGACY_DM_MODAL_DEFAULT_H;
  const rect: PersistedDMModalRect = {
    x: Number.isFinite(x) ? x : fallback.rect.x,
    y: Number.isFinite(y) ? y : fallback.rect.y,
    w: isLegacyDefaultRect
      ? DM_MODAL_MIN_W
      : Number.isFinite(w) ? Math.max(DM_MODAL_MIN_W, w) : fallback.rect.w,
    h: isLegacyDefaultRect
      ? DM_MODAL_MIN_H
      : Number.isFinite(h) ? Math.max(DM_MODAL_MIN_H, h) : fallback.rect.h,
  };
  const pinned = typeof parsed.pinned === 'boolean' ? parsed.pinned : fallback.pinned;
  const opacityRaw = Number(parsed.opacity);
  const opacity = Number.isFinite(opacityRaw)
    ? Math.min(1, Math.max(0.35, opacityRaw))
    : fallback.opacity;

  return {
    open,
    rect,
    pinned,
    opacity,
  };
}

function loadPersistedDMModalState(): PersistedDMModalState {
  const fallback = createDefaultDMModalState();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(getScopedWidgetModalStateKey(WIDGET_DM_MODAL_STATE_KEY));
    if (!raw) return fallback;
    return normalizeDMModalState(JSON.parse(raw), fallback);
  } catch {
    return fallback;
  }
}

function savePersistedDMModalState(state: PersistedDMModalState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getScopedWidgetModalStateKey(WIDGET_DM_MODAL_STATE_KEY),
      JSON.stringify(state),
    );
  } catch {
    // 스토리지 접근 불가 시 무시
  }
}

function createDefaultAgentModalState(): PersistedAgentModalState {
  return {
    open: false,
    rect: { x: 60, y: 0, w: AGENT_MODAL_DEFAULT_W, h: AGENT_MODAL_DEFAULT_H },
    pinned: false,
    opacity: 1,
  };
}

function normalizeAgentModalState(
  raw: unknown,
  fallback: PersistedAgentModalState,
): PersistedAgentModalState {
  if (!raw || typeof raw !== 'object') {
    return {
      ...fallback,
      rect: { ...fallback.rect },
    };
  }

  const parsed = raw as Record<string, unknown>;
  const open = typeof parsed.open === 'boolean' ? parsed.open : fallback.open;
  const rectRaw =
    parsed.rect && typeof parsed.rect === 'object'
      ? (parsed.rect as Record<string, unknown>)
      : {};
  const x = Number(rectRaw.x);
  const y = Number(rectRaw.y);
  const w = Number(rectRaw.w);
  const h = Number(rectRaw.h);
  const rect: PersistedAgentModalRect = {
    x: Number.isFinite(x) ? x : fallback.rect.x,
    y: Number.isFinite(y) ? y : fallback.rect.y,
    w: Number.isFinite(w) ? Math.max(AGENT_MODAL_MIN_W, w) : fallback.rect.w,
    h: Number.isFinite(h) ? Math.max(AGENT_MODAL_MIN_H, h) : fallback.rect.h,
  };
  const pinned = typeof parsed.pinned === 'boolean' ? parsed.pinned : fallback.pinned;
  const opacityRaw = Number(parsed.opacity);
  const opacity = Number.isFinite(opacityRaw)
    ? Math.min(1, Math.max(0.35, opacityRaw))
    : fallback.opacity;

  return {
    open,
    rect,
    pinned,
    opacity,
  };
}

function loadPersistedAgentModalState(): PersistedAgentModalState {
  const fallback = createDefaultAgentModalState();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(getScopedWidgetModalStateKey(WIDGET_AGENT_MODAL_STATE_KEY));
    if (!raw) return fallback;
    return normalizeAgentModalState(JSON.parse(raw), fallback);
  } catch {
    return fallback;
  }
}

function savePersistedAgentModalState(state: PersistedAgentModalState) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getScopedWidgetModalStateKey(WIDGET_AGENT_MODAL_STATE_KEY),
      JSON.stringify(state),
    );
  } catch {
    // 스토리지 접근 불가 시 무시
  }
}

function normalizeWidgetLayoutMeta(raw: unknown): WidgetLayoutMeta {
  if (!raw || typeof raw !== 'object') {
    return { ...DEFAULT_WIDGET_LAYOUT_META };
  }
  const parsed = raw as Record<string, unknown>;
  const layoutModeRaw = parsed.layoutMode ?? parsed.layout_mode;
  const templateSourceEmailRaw = parsed.templateSourceEmail ?? parsed.template_source_email;
  return {
    layoutMode: layoutModeRaw === 'default' ? 'default' : 'custom',
    templateSourceEmail:
      typeof templateSourceEmailRaw === 'string' && templateSourceEmailRaw.trim()
        ? templateSourceEmailRaw.trim()
        : null,
  };
}

function loadPersistedWidgetLayoutMeta(): WidgetLayoutMeta {
  if (typeof window === 'undefined') return { ...DEFAULT_WIDGET_LAYOUT_META };
  try {
    const raw = window.localStorage.getItem(getScopedWidgetModalStateKey(WIDGET_LAYOUT_META_STATE_KEY));
    if (!raw) return { ...DEFAULT_WIDGET_LAYOUT_META };
    return normalizeWidgetLayoutMeta(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_WIDGET_LAYOUT_META };
  }
}

function savePersistedWidgetLayoutMeta(meta: WidgetLayoutMeta) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      getScopedWidgetModalStateKey(WIDGET_LAYOUT_META_STATE_KEY),
      JSON.stringify({
        layout_mode: meta.layoutMode,
        template_source_email: meta.templateSourceEmail,
      }),
    );
  } catch {
    // 스토리지 접근 불가 시 무시
  }
}

function cloneFloatingWindow(window: FloatingWindow): FloatingWindow {
  return {
    ...window,
    rect: { ...window.rect },
    memo: {
      ...window.memo,
      imageStore: { ...window.memo.imageStore },
    },
  };
}

function cloneCalendarModalState(state: PersistedCalendarModalState): PersistedCalendarModalState {
  return {
    ...state,
    rect: { ...state.rect },
    selectedProviders: [...state.selectedProviders],
  };
}

function cloneEmailModalState(state: PersistedEmailModalState): PersistedEmailModalState {
  return {
    ...state,
    rect: { ...state.rect },
  };
}

function cloneDMModalState(state: PersistedDMModalState): PersistedDMModalState {
  return {
    ...state,
    rect: { ...state.rect },
  };
}

function cloneAgentModalState(state: PersistedAgentModalState): PersistedAgentModalState {
  return {
    ...state,
    rect: { ...state.rect },
  };
}

function createWidgetCanvasSnapshot(
  windows: FloatingWindow[],
  calendarModal: PersistedCalendarModalState,
  emailModal: PersistedEmailModalState,
  dmModal: PersistedDMModalState,
  agentModal: PersistedAgentModalState,
): WidgetCanvasSnapshot {
  return {
    windows: windows.map(cloneFloatingWindow),
    calendarModal: cloneCalendarModalState(calendarModal),
    emailModal: cloneEmailModalState(emailModal),
    dmModal: cloneDMModalState(dmModal),
    agentModal: cloneAgentModalState(agentModal),
  };
}

function createSuppressedWidgetCanvasSnapshot(snapshot: WidgetCanvasSnapshot): WidgetCanvasSnapshot {
  return {
    windows: [],
    calendarModal: { ...cloneCalendarModalState(snapshot.calendarModal), open: false },
    emailModal: { ...cloneEmailModalState(snapshot.emailModal), open: false },
    dmModal: { ...cloneDMModalState(snapshot.dmModal), open: false },
    agentModal: { ...cloneAgentModalState(snapshot.agentModal), open: false },
  };
}

function createDefaultWidgetLayoutMeta(): WidgetLayoutMeta {
  return {
    layoutMode: 'default',
    templateSourceEmail: null,
  };
}

function createDefaultWidgetCanvasSnapshot(): WidgetCanvasSnapshot {
  return {
    windows: [],
    calendarModal: {
      open: true,
      selectedDate: todayDateKey(),
      selectedProviders: [],
      rect: {
        x: -375.82421875,
        y: -351.583984375,
        w: 863.1640625,
        h: 542.83203125,
      },
      pinned: true,
      opacity: 0.7,
    },
    emailModal: {
      open: true,
      initialTab: 'gmail',
      rect: {
        x: -174.857421875,
        y: 232.626953125,
        w: 464.91796875,
        h: 597.68359375,
      },
      pinned: true,
      opacity: 0.7,
    },
    dmModal: {
      open: true,
      rect: {
        x: -596.4140625,
        y: 233.80859375,
        w: 420,
        h: 597.5546875,
      },
      pinned: true,
      opacity: 0.65,
    },
    agentModal: {
      open: true,
      rect: {
        x: 430.9375,
        y: -44.6171875,
        w: 780,
        h: 1156.765625,
      },
      pinned: true,
      opacity: 0.7,
    },
  };
}

function isEmptyWidgetCanvasSnapshot(snapshot: WidgetCanvasSnapshot): boolean {
  return snapshot.windows.length === 0
    && !snapshot.calendarModal.open
    && !snapshot.emailModal.open
    && !snapshot.dmModal.open
    && !snapshot.agentModal.open;
}

function createWidgetLayoutSignature(snapshot: WidgetCanvasSnapshot): string {
  return JSON.stringify({
    windows: snapshot.windows.map(toPersistedWindow),
    calendarModal: {
      ...snapshot.calendarModal,
      rect: { ...snapshot.calendarModal.rect },
      selectedProviders: [...snapshot.calendarModal.selectedProviders],
    },
    emailModal: {
      ...snapshot.emailModal,
      rect: { ...snapshot.emailModal.rect },
    },
    dmModal: {
      ...snapshot.dmModal,
      rect: { ...snapshot.dmModal.rect },
    },
    agentModal: {
      ...snapshot.agentModal,
      rect: { ...snapshot.agentModal.rect },
    },
  });
}

function nextMemoPosition(
  existing: FloatingWindow[],
  bounds: CanvasBounds,
): { x: number; y: number } {
  const isMobile = bounds.w <= 768;
  if (isMobile) {
    const pad = 12;
    return {
      x: pad,
      y: pad + existing.length * 8,
    };
  }
  const base = { x: 80, y: 60 };
  const offset = existing.length * STAGGER;
  return {
    x: Math.min(base.x + offset, Math.max(0, bounds.w - MEMO_DEFAULT_W - 20)),
    y: Math.min(base.y + offset, Math.max(0, bounds.h - MEMO_DEFAULT_H - 20)),
  };
}

function toPersistedWindow(window: FloatingWindow): PersistedMemoWindow {
  return {
    id: window.id,
    type: 'memo',
    rect: window.rect,
    pinned: window.pinned,
    opacity: window.opacity,
    zIndex: window.zIndex,
    memo: {
      id: window.memo.id,
      content: window.memo.content,
    },
  };
}

function fromPersistedWindow(window: PersistedMemoWindow): FloatingWindow {
  return {
    id: window.id,
    type: 'memo',
    rect: window.rect,
    pinned: window.pinned,
    opacity: Math.min(1, Math.max(0.35, window.opacity ?? 1)),
    zIndex: window.zIndex,
    memo: {
      id: window.memo.id,
      content: window.memo.content,
      imageStore: {},
    },
  };
}

function collectUsedImageIds(windows: FloatingWindow[]): string[] {
  const used = new Set<string>();
  for (const window of windows) {
    for (const id of extractMemoImageIds(window.memo.content)) {
      used.add(id);
    }
  }
  return [...used];
}

function mergeImageStoreFromMap(
  windows: FloatingWindow[],
  imageMap: Record<string, string>,
): FloatingWindow[] {
  let changed = false;
  const next = windows.map((window) => {
    const ids = extractMemoImageIds(window.memo.content);
    if (ids.length === 0) return window;

    const imageStore = { ...window.memo.imageStore };
    let localChanged = false;
    for (const id of ids) {
      const data = imageMap[id];
      if (!data || imageStore[id]) continue;
      imageStore[id] = data;
      localChanged = true;
    }

    if (!localChanged) return window;
    changed = true;
    return { ...window, memo: { ...window.memo, imageStore } };
  });
  return changed ? next : windows;
}

interface RemoteMemoData {
  id: string;
  content: string;
  image_store?: Record<string, string>;
}

interface RemoteMemoWindow {
  id: string;
  type: 'memo';
  rect: FloatingRect;
  pinned: boolean;
  opacity: number;
  zIndex: number;
  memo: RemoteMemoData;
}

interface RemoteEmailModalState {
  open: boolean;
  initial_tab: MailProvider;
  rect: PersistedEmailModalRect;
  pinned: boolean;
  opacity: number;
}

interface RemoteCalendarModalState {
  open: boolean;
  selected_date: string;
  selected_providers: CalendarProviderId[];
  rect: PersistedCalendarModalRect;
  pinned: boolean;
  opacity: number;
}

interface RemoteDMModalState {
  open: boolean;
  rect: PersistedDMModalRect;
  pinned: boolean;
  opacity: number;
}

interface RemoteAgentModalState {
  open: boolean;
  rect: PersistedAgentModalRect;
  pinned: boolean;
  opacity: number;
}

interface RemoteLayoutMeta {
  layout_mode?: WidgetLayoutMode;
  template_source_email?: string | null;
}

interface RemoteCanvasPayload {
  version: typeof REMOTE_CANVAS_VERSION;
  windows: RemoteMemoWindow[];
  calendar_modal: RemoteCalendarModalState;
  email_modal: RemoteEmailModalState;
  dm_modal: RemoteDMModalState;
  agent_modal: RemoteAgentModalState;
  layout_meta?: RemoteLayoutMeta;
}

function toRemotePayload(
  windows: FloatingWindow[],
  calendarModal: PersistedCalendarModalState,
  emailModal: PersistedEmailModalState,
  dmModal: PersistedDMModalState,
  agentModal: PersistedAgentModalState,
  layoutMeta: WidgetLayoutMeta,
): RemoteCanvasPayload {
  return {
    version: REMOTE_CANVAS_VERSION,
    windows: windows.map((window) => {
      const used = new Set(extractMemoImageIds(window.memo.content));
      const imageStore: Record<string, string> = {};
      for (const [id, dataUrl] of Object.entries(window.memo.imageStore)) {
        if (!used.has(id)) continue;
        imageStore[id] = dataUrl;
      }
      return {
        id: window.id,
        type: 'memo',
        rect: window.rect,
        pinned: window.pinned,
        opacity: window.opacity,
        zIndex: window.zIndex,
        memo: {
          id: window.memo.id,
          content: window.memo.content,
          image_store: imageStore,
        },
      };
    }),
    calendar_modal: {
      open: calendarModal.open,
      selected_date: calendarModal.selectedDate,
      selected_providers: calendarModal.selectedProviders,
      rect: {
        x: calendarModal.rect.x,
        y: calendarModal.rect.y,
        w: Math.max(CALENDAR_MODAL_MIN_W, calendarModal.rect.w),
        h: Math.max(CALENDAR_MODAL_MIN_H, calendarModal.rect.h),
      },
      pinned: calendarModal.pinned,
      opacity: Math.min(1, Math.max(0.35, calendarModal.opacity)),
    },
    email_modal: {
      open: emailModal.open,
      initial_tab: emailModal.initialTab,
      rect: {
        x: emailModal.rect.x,
        y: emailModal.rect.y,
        w: Math.max(EMAIL_MODAL_MIN_W, emailModal.rect.w),
        h: Math.max(EMAIL_MODAL_MIN_H, emailModal.rect.h),
      },
      pinned: emailModal.pinned,
      opacity: Math.min(1, Math.max(0.35, emailModal.opacity)),
    },
    dm_modal: {
      open: dmModal.open,
      rect: {
        x: dmModal.rect.x,
        y: dmModal.rect.y,
        w: Math.max(DM_MODAL_MIN_W, dmModal.rect.w),
        h: Math.max(DM_MODAL_MIN_H, dmModal.rect.h),
      },
      pinned: dmModal.pinned,
      opacity: Math.min(1, Math.max(0.35, dmModal.opacity)),
    },
    agent_modal: {
      open: agentModal.open,
      rect: {
        x: agentModal.rect.x,
        y: agentModal.rect.y,
        w: Math.max(AGENT_MODAL_MIN_W, agentModal.rect.w),
        h: Math.max(AGENT_MODAL_MIN_H, agentModal.rect.h),
      },
      pinned: agentModal.pinned,
      opacity: Math.min(1, Math.max(0.35, agentModal.opacity)),
    },
    layout_meta: {
      layout_mode: layoutMeta.layoutMode,
      template_source_email: layoutMeta.templateSourceEmail,
    },
  };
}

function parseRemotePayload(
  payload: unknown,
  fallbackCalendarModal: PersistedCalendarModalState,
  fallbackEmailModal: PersistedEmailModalState,
  fallbackDMModal: PersistedDMModalState,
  fallbackAgentModal: PersistedAgentModalState,
): {
  windows: FloatingWindow[];
  calendarModal: PersistedCalendarModalState;
  emailModal: PersistedEmailModalState;
  dmModal: PersistedDMModalState;
  agentModal: PersistedAgentModalState;
  layoutMeta: WidgetLayoutMeta;
} | null {
  if (!payload || typeof payload !== 'object') return null;
  const record = payload as Record<string, unknown>;
  const version = Number(record.version);
  if (version !== REMOTE_CANVAS_VERSION) return null;
  if (!Array.isArray(record.windows)) return null;

  const rawCalendarModal = record.calendar_modal;
  const rawEmailModal = record.email_modal;
  if (!rawEmailModal || typeof rawEmailModal !== 'object') return null;
  const rawDMModal = record.dm_modal;
  const rawAgentModal = record.agent_modal;

  const out: FloatingWindow[] = [];
  for (const raw of record.windows) {
    if (!raw || typeof raw !== 'object') continue;
    const item = raw as Record<string, unknown>;
    if (item.type !== 'memo') continue;
    if (typeof item.id !== 'string' || !item.id) continue;
    if (typeof item.pinned !== 'boolean') continue;
    if (typeof item.zIndex !== 'number' || !Number.isFinite(item.zIndex)) continue;
    const opacityRaw = Number(item.opacity);
    const opacity = Number.isFinite(opacityRaw) ? Math.min(1, Math.max(0.35, opacityRaw)) : 1;
    const rectRaw = item.rect as Record<string, unknown> | undefined;
    const memoRaw = item.memo as Record<string, unknown> | undefined;
    if (!rectRaw || !memoRaw) continue;
    const x = Number(rectRaw.x);
    const y = Number(rectRaw.y);
    const w = Number(rectRaw.w);
    const h = Number(rectRaw.h);
    if (![x, y, w, h].every(Number.isFinite)) continue;
    if (typeof memoRaw.id !== 'string' || typeof memoRaw.content !== 'string') continue;

    const imageStoreRaw = memoRaw.image_store as Record<string, unknown> | undefined;
    const imageStore: Record<string, string> = {};
    if (imageStoreRaw && typeof imageStoreRaw === 'object') {
      for (const [imgId, data] of Object.entries(imageStoreRaw)) {
        if (!imgId || typeof data !== 'string' || !data.startsWith('data:image/')) continue;
        imageStore[imgId] = data;
      }
    }

    out.push({
      id: item.id,
      type: 'memo',
      rect: { x, y, w, h },
      pinned: item.pinned,
      opacity,
      zIndex: item.zIndex,
      memo: {
        id: memoRaw.id,
        content: memoRaw.content,
        imageStore,
      },
    });
  }
  return {
    windows: out,
    calendarModal: normalizeCalendarModalState(rawCalendarModal, fallbackCalendarModal),
    emailModal: normalizeEmailModalState(rawEmailModal, fallbackEmailModal),
    dmModal: normalizeDMModalState(rawDMModal, fallbackDMModal),
    agentModal: normalizeAgentModalState(rawAgentModal, fallbackAgentModal),
    layoutMeta: normalizeWidgetLayoutMeta(record.layout_meta),
  };
}

/* ------------------------------------------------------------------ */
/* Keyframes                                                           */
/* ------------------------------------------------------------------ */

const dockBounce = keyframes`
  0%   { transform: translateY(0) rotateX(0); }
  25%  { transform: translateY(-18px) rotateX(-6deg); }
  50%  { transform: translateY(-4px) rotateX(-2deg); }
  65%  { transform: translateY(-10px) rotateX(-4deg); }
  80%  { transform: translateY(-2px) rotateX(0); }
  100% { transform: translateY(0) rotateX(0); }
`;

const dockRise = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 20px);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0);
  }
`;

const tooltipIn = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(4px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

/* ------------------------------------------------------------------ */
/* Styled Components                                                   */
/* ------------------------------------------------------------------ */

const Canvas = styled.div`
  flex: 1;
  min-height: 0;
  background: transparent;
  position: relative;
  overflow: hidden;
`;

const CanvasStage = styled.div<{ $width: number; $panX: number; $zIndex: number }>`
  position: absolute;
  inset: 0 auto 0 50%;
  width: ${({ $width }) => `${$width}px`};
  transform: translateX(-50%) translateX(${({ $panX }) => `${$panX}px`});
  z-index: ${({ $zIndex }) => $zIndex};
  will-change: transform;
`;

const PanOverlay = styled.div<{ $panning: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 70;
  cursor: ${({ $panning }) => ($panning ? 'grabbing' : 'grab')};
  touch-action: none;
  user-select: none;
`;

const FloatingLayer = styled.div<{ $panReady: boolean; $panning: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: auto;
  cursor: ${({ $panReady, $panning }) =>
    $panReady ? ($panning ? 'grabbing' : 'grab') : 'default'};
  touch-action: none;
  user-select: none;

  & > * {
    pointer-events: auto;
  }
`;

const DockWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 2px;
  position: absolute;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  z-index: 80;
  perspective: 800px;
  animation: ${dockRise} 540ms 60ms cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const DockBar = styled.div`
  display: flex;
  align-items: flex-end;
  padding: ${DOCK_PAD_Y}px ${DOCK_PAD_X}px;
  gap: ${ICON_GAP}px;
  border-radius: 22px;
  position: relative;
  overflow: visible;

  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'rgba(28, 28, 30, 0.62)'
      : 'rgba(240, 240, 240, 0.55)'};
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  border: 1px solid
    ${({ theme }) =>
      theme.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(255, 255, 255, 0.65)'};
  box-shadow:
    ${({ theme }) =>
      theme.mode === 'dark'
        ? `0 8px 28px rgba(0, 0, 0, 0.4),
           0 2px 8px rgba(0, 0, 0, 0.26),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.04)`
        : `0 8px 32px rgba(0, 0, 0, 0.08),
           0 2px 8px rgba(0, 0, 0, 0.04),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.9)`};
`;

const DockDivider = styled.div`
  width: 1px;
  height: 34px;
  margin: 0 2px;
  align-self: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'};
`;

const ItemOuter = styled.div<{
  $bouncing: boolean;
  $hovered: boolean;
  $neighborDir: -1 | 0 | 1;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

const ItemVisual = styled.div<{
  $bouncing: boolean;
  $hovered: boolean;
  $neighborDir: -1 | 0 | 1;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  transform-style: preserve-3d;
  will-change: transform;

  transition: transform 0.35s cubic-bezier(0.25, 1, 0.5, 1);

  transform: ${({ $hovered, $neighborDir, $bouncing, $disabled }) => {
    if ($disabled) return 'translateY(0) rotateX(0)';
    if ($bouncing) return 'none';
    if ($hovered) return `translateY(${LIFT_Y}px) rotateX(-${TILT_DEG}deg) scale(1.02)`;
    if ($neighborDir !== 0)
      return `translateY(${LIFT_Y * 0.25}px) rotateY(${$neighborDir * NEIGHBOR_TILT_DEG}deg)`;
    return 'translateY(0) rotateX(0)';
  }};

  ${({ $bouncing, $disabled }) =>
    $bouncing &&
    !$disabled &&
    css`
      animation: ${dockBounce} 0.5s cubic-bezier(0.28, 0.84, 0.42, 1);
    `}
`;

const IconSquare = styled.div<{ $hovered: boolean; $active?: boolean; $disabled?: boolean }>`
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  border-radius: ${ICON_RADIUS};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;

  color: ${({ theme, $active }) =>
    $active
      ? '#f59e0b'
      : theme.mode === 'dark' ? '#bdbdc2' : '#3a3a3c'};

  transition: color 0.25s ease,
    box-shadow 0.35s cubic-bezier(0.25, 1, 0.5, 1),
    filter 0.35s ease-out;

  filter: none;
  opacity: ${({ $disabled }) => ($disabled ? 0.72 : 1)};

  background: ${({ theme, $active }) =>
    $active
      ? theme.mode === 'dark'
        ? 'linear-gradient(145deg, #444447 0%, #343437 100%)'
        : 'linear-gradient(145deg, #fff3d8 0%, #ffe3a3 100%)'
      : theme.mode === 'dark'
        ? 'linear-gradient(145deg, #343436 0%, #28282a 100%)'
        : 'linear-gradient(145deg, #ffffff 0%, #f5f5f7 100%)'};

  box-shadow: ${({ theme, $hovered, $active }) =>
    $hovered || $active
      ? theme.mode === 'dark'
        ? `0 4px 12px rgba(0, 0, 0, 0.34),
           0 12px 30px rgba(0, 0, 0, 0.28),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.05)`
        : `0 4px 12px rgba(0, 0, 0, 0.1),
           0 16px 40px rgba(0, 0, 0, 0.08),
           0 0 0 0.5px rgba(0, 0, 0, 0.04)`
      : theme.mode === 'dark'
        ? `0 2px 6px rgba(0, 0, 0, 0.32),
           0 8px 20px rgba(0, 0, 0, 0.22),
           inset 0 0.5px 0 rgba(255, 255, 255, 0.04)`
        : `0 1px 3px rgba(0, 0, 0, 0.06),
           0 6px 20px rgba(0, 0, 0, 0.05),
           0 0 0 0.5px rgba(0, 0, 0, 0.04)`};

  & > svg {
    display: block;
    flex-shrink: 0;
    backface-visibility: hidden;
    transform: translateZ(0);
    shape-rendering: geometricPrecision;
  }
`;

const IconImg = styled.img<{ $contain?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: ${({ $contain }) => ($contain ? 'contain' : 'cover')};
  border-radius: inherit;
  padding: ${({ $contain }) => ($contain ? '5px' : '0')};
`;

const TooltipBubble = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 24px);
  left: 50%;
  transform: translateX(-50%);
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;

  background: ${({ theme }) =>
    theme.mode === 'dark' ? '#1c1c1e' : '#18181b'};
  color: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.14)'};
  backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  text-rendering: geometricPrecision;

  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  animation: ${tooltipIn} 0.15s ease-out;
`;

const DockReflection = styled.div`
  width: 65%;
  height: 2px;
  margin-top: 2px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
  background: ${({ theme }) =>
    theme.mode === 'dark'
      ? 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 30%, rgba(255,255,255,0.03) 70%, transparent 100%)'
      : 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.03) 30%, rgba(0,0,0,0.03) 70%, transparent 100%)'};
`;

const WidgetStartOverlay = styled(EmptyState)`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const WidgetPage: React.FC = () => {
  const { t } = useTranslation('widget');
  const { openWorkspaceMode } = useWorkspaceNavigate();
  const { isEnabled } = useFeatureFlags();
  /* Phase 11-5: filter dock items by tenant flag.  We compute the
   * effective list once per render so layout / drag-drop / hover
   * state stay aligned with the rendered icons. */
  const visibleDockApps = React.useMemo(
    () => DOCK_APPS.filter((item) => isEnabled(DOCK_FEATURE_ID[item.id])),
    [isEnabled],
  );
  const visibleDockItems = React.useMemo(
    () => [...visibleDockApps, { id: 'pan', label: 'dock.panCanvas', icon: Hand } as DockItemConfig],
    [visibleDockApps],
  );
  /* Tenant gate for cloud-synced canvas snapshots.  When off, the dock
   * still loads/saves the local indexed-db snapshot but no remote
   * pull/push happens.  Wired into `widgetPersistence` via context
   * below — for now we keep the local-only path. */
  const cloudSyncEnabled = isEnabled('mode.widget.cloudSync');
  const onboardingPending = useUserStore((s) => s.user?.onboardingCompleted === false);
  const onboardingUserId = useUserStore((s) => s.user?.userId ?? null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [layoutMeta, setLayoutMeta] = useState<WidgetLayoutMeta>(() => loadPersistedWidgetLayoutMeta());
  const layoutMetaRef = useRef<WidgetLayoutMeta>(layoutMeta);
  const [defaultLayoutSuppressed, setDefaultLayoutSuppressed] = useState(false);
  const defaultLayoutSuppressedRef = useRef(defaultLayoutSuppressed);
  const suppressedDefaultSnapshotRef = useRef<WidgetCanvasSnapshot | null>(null);
  const defaultLayoutBaselineKeyRef = useRef<string | null>(null);
  const [onboardingIntroPhase, setOnboardingIntroPhase] = useState<'dock' | 'modals' | 'done'>('done');

  /* ---- Dock state ---- */
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [bouncingId, setBouncingId] = useState<string | null>(null);
  const [panMode, setPanMode] = useState(false);
  const [panX, setPanX] = useState(0);
  const [isPanning, setIsPanning] = useState(false);
  const [viewportBounds, setViewportBounds] = useState<CanvasBounds>(() => ({
    w: typeof window !== 'undefined' ? Math.max(320, window.innerWidth) : WIDGET_STAGE_MAX_W,
    h: typeof window !== 'undefined' ? Math.max(360, window.innerHeight) : 800,
  }));
  const [calendarModal, setCalendarModal] = useState<PersistedCalendarModalState>(() =>
    loadPersistedCalendarModalState(),
  );
  const calendarModalRef = useRef<PersistedCalendarModalState>(calendarModal);
  const [emailModal, setEmailModal] = useState<PersistedEmailModalState>(() =>
    loadPersistedEmailModalState(),
  );
  const emailModalRef = useRef<PersistedEmailModalState>(emailModal);
  const [dmModal, setDmModal] = useState<PersistedDMModalState>(() =>
    loadPersistedDMModalState(),
  );
  const dmModalRef = useRef<PersistedDMModalState>(dmModal);
  const [agentModal, setAgentModal] = useState<PersistedAgentModalState>(() =>
    loadPersistedAgentModalState(),
  );
  const agentModalRef = useRef<PersistedAgentModalState>(agentModal);
  const [modalZOrder, setModalZOrder] = useState<Record<WidgetModalType, number>>(
    () => ({ ...DEFAULT_WIDGET_MODAL_Z_ORDER }),
  );
  const modalZOrderRef = useRef(modalZOrder);

  /* ---- Floating windows state ---- */
  const [windows, setWindows] = useState<FloatingWindow[]>([]);
  const windowsRef = useRef<FloatingWindow[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const topZ = useRef(Math.max(10, ...Object.values(DEFAULT_WIDGET_MODAL_Z_ORDER)));
  const pendingMovesRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const moveRafRef = useRef<number | null>(null);
  const persistTimerRef = useRef<number | null>(null);
  const lastPruneAtRef = useRef(0);
  const cloudSaveTimerRef = useRef<number | null>(null);
  const calendarModalPersistTimerRef = useRef<number | null>(null);
  const emailModalPersistTimerRef = useRef<number | null>(null);
  const dmModalPersistTimerRef = useRef<number | null>(null);
  const agentModalPersistTimerRef = useRef<number | null>(null);
  const cloudUpdatedAtRef = useRef(0);
  const panDragRef = useRef<{ startX: number; basePanX: number } | null>(null);

  const viewportWidth = Math.max(320, viewportBounds.w);
  const viewportHeight = Math.max(360, viewportBounds.h);
  const stageWidth = viewportWidth <= WIDGET_STAGE_MOBILE_BREAKPOINT
    ? viewportWidth
    : WIDGET_STAGE_MAX_W;
  const hasOverflowX = stageWidth > viewportWidth;
  const panLimit = hasOverflowX ? (stageWidth - viewportWidth) / 2 : 0;
  const stageBounds: CanvasBounds = { w: stageWidth, h: viewportHeight };
  const modalHostBounds = { width: stageBounds.w, height: stageBounds.h };
  const shouldSuppressWidgetTemplate = useCallback(
    (meta: WidgetLayoutMeta) => (
      meta.layoutMode === 'default'
      && viewportWidth <= WIDGET_STAGE_MAX_W
    ),
    [viewportWidth],
  );

  const getCanvasBounds = useCallback((): CanvasBounds => {
    const el = canvasRef.current;
    return {
      w: el?.clientWidth ?? stageBounds.w,
      h: el?.clientHeight ?? stageBounds.h,
    };
  }, [stageBounds.h, stageBounds.w]);

  const clampPanPosition = useCallback(
    (nextPan: number) => Math.min(panLimit, Math.max(-panLimit, nextPan)),
    [panLimit],
  );

  const modalRectToStageRect = useCallback(
    (rect: { x: number; y: number; w: number; h: number }): FloatingRect => ({
      x: (stageWidth / 2) + rect.x - (rect.w / 2),
      y: (stageBounds.h / 2) + rect.y - (rect.h / 2),
      w: rect.w,
      h: rect.h,
    }),
    [stageBounds.h, stageWidth],
  );

  const ensureStageRectVisible = useCallback(
    (rect: { x: number; w: number }) => {
      if (!hasOverflowX) return;
      setPanX((prev) => {
        const gutter = 32;
        const visibleLeft = ((stageWidth - viewportWidth) / 2) - prev;
        const visibleRight = visibleLeft + viewportWidth;
        const targetLeft = Math.max(0, rect.x - gutter);
        const targetRight = Math.min(stageWidth, rect.x + rect.w + gutter);

        if (targetLeft < visibleLeft) {
          return clampPanPosition(((stageWidth - viewportWidth) / 2) - targetLeft);
        }
        if (targetRight > visibleRight) {
          return clampPanPosition(((stageWidth - viewportWidth) / 2) - (targetRight - viewportWidth));
        }
        return prev;
      });
    },
    [clampPanPosition, hasOverflowX, stageWidth, viewportWidth],
  );

  const clampRectToBounds = useCallback((rect: FloatingRect, bounds: CanvasBounds): FloatingRect => {
    const minW = Math.min(MEMO_MIN_W, bounds.w);
    const minH = Math.min(MEMO_MIN_H, bounds.h);
    const w = Math.min(Math.max(rect.w, minW), bounds.w);
    const h = Math.min(Math.max(rect.h, minH), bounds.h);
    const maxX = Math.max(0, bounds.w - w);
    const maxY = Math.max(0, bounds.h - h);
    return {
      x: Math.min(Math.max(rect.x, 0), maxX),
      y: Math.min(Math.max(rect.y, 0), maxY),
      w,
      h,
    };
  }, []);

  const clampAllWindows = useCallback(
    (list: FloatingWindow[], bounds: CanvasBounds): FloatingWindow[] => {
      let changed = false;
      const next = list.map((w) => {
        const rect = clampRectToBounds(w.rect, bounds);
        if (
          rect.x === w.rect.x &&
          rect.y === w.rect.y &&
          rect.w === w.rect.w &&
          rect.h === w.rect.h
        ) {
          return w;
        }
        changed = true;
        return { ...w, rect };
      });
      return changed ? next : list;
    },
    [clampRectToBounds],
  );

  const syncTopLayerZ = useCallback((nextWindows: FloatingWindow[]) => {
    topZ.current = Math.max(
      10,
      ...Object.values(modalZOrderRef.current),
      ...nextWindows.map((window) => window.zIndex),
    );
  }, []);

  const applyWidgetCanvasSnapshot = useCallback((snapshot: WidgetCanvasSnapshot) => {
    const nextWindows = snapshot.windows.map(cloneFloatingWindow);
    const nextCalendarModal = cloneCalendarModalState(snapshot.calendarModal);
    const nextEmailModal = cloneEmailModalState(snapshot.emailModal);
    const nextDMModal = cloneDMModalState(snapshot.dmModal);
    const nextAgentModal = cloneAgentModalState(snapshot.agentModal);

    setWindows(nextWindows);
    windowsRef.current = nextWindows;
    syncTopLayerZ(nextWindows);

    setCalendarModal(nextCalendarModal);
    calendarModalRef.current = nextCalendarModal;

    setEmailModal(nextEmailModal);
    emailModalRef.current = nextEmailModal;

    setDmModal(nextDMModal);
    dmModalRef.current = nextDMModal;

    setAgentModal(nextAgentModal);
    agentModalRef.current = nextAgentModal;
  }, [syncTopLayerZ]);

  const captureWidgetCanvasSnapshot = useCallback(
    () =>
      createWidgetCanvasSnapshot(
        windowsRef.current,
        calendarModalRef.current,
        emailModalRef.current,
        dmModalRef.current,
        agentModalRef.current,
      ),
    [],
  );

  const releaseSuppressedDefaultLayoutForInteraction = useCallback(() => {
    if (!defaultLayoutSuppressed) return;
    suppressedDefaultSnapshotRef.current = null;
    setDefaultLayoutSuppressed(false);
    setLayoutMeta((prev) => (
      prev.layoutMode === 'custom'
        ? prev
        : { ...prev, layoutMode: 'custom' }
    ));
  }, [defaultLayoutSuppressed]);

  const flushPendingMoves = useCallback(() => {
    const pending = pendingMovesRef.current;
    pendingMovesRef.current = new Map();
    moveRafRef.current = null;
    if (pending.size === 0) return;

    const bounds = getCanvasBounds();
    setWindows((prev) =>
      prev.map((w) => {
        const p = pending.get(w.id);
        if (!p) return w;
        return {
          ...w,
          rect: clampRectToBounds({ ...w.rect, x: p.x, y: p.y }, bounds),
        };
      }),
    );
  }, [getCanvasBounds, clampRectToBounds]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const updateViewportBounds = () => {
      setViewportBounds({
        w: Math.max(320, el.clientWidth || window.innerWidth),
        h: Math.max(360, el.clientHeight || window.innerHeight),
      });
    };

    updateViewportBounds();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateViewportBounds);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasOverflowX) {
      if (panMode) setPanMode(false);
      if (panX !== 0) setPanX(0);
      return;
    }
    setPanX((prev) => clampPanPosition(prev));
  }, [clampPanPosition, hasOverflowX, panMode, panX]);

  useEffect(() => {
    if (!panMode) return undefined;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPanMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panMode]);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      const drag = panDragRef.current;
      if (!drag) return;
      setPanX(clampPanPosition(drag.basePanX + (e.clientX - drag.startX)));
    };

    const handlePointerUp = () => {
      if (!panDragRef.current) return;
      panDragRef.current = null;
      setIsPanning(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      handlePointerUp();
    };
  }, [clampPanPosition]);

  useEffect(() => {
    windowsRef.current = windows;
  }, [windows]);

  useEffect(() => {
    modalZOrderRef.current = modalZOrder;
    syncTopLayerZ(windowsRef.current);
  }, [modalZOrder, syncTopLayerZ]);

  useEffect(() => {
    if (!onboardingPending || hydrated) return;
    const currentSnapshot = createWidgetCanvasSnapshot(
      windowsRef.current,
      calendarModalRef.current,
      emailModalRef.current,
      dmModalRef.current,
      agentModalRef.current,
    );
    if (!isEmptyWidgetCanvasSnapshot(currentSnapshot)) return;

    const previewLayoutMeta = createDefaultWidgetLayoutMeta();
    const previewSnapshot = createDefaultWidgetCanvasSnapshot();
    setLayoutMeta(previewLayoutMeta);
    layoutMetaRef.current = previewLayoutMeta;
    defaultLayoutBaselineKeyRef.current = createWidgetLayoutSignature(previewSnapshot);
    if (shouldSuppressWidgetTemplate(previewLayoutMeta)) {
      suppressedDefaultSnapshotRef.current = previewSnapshot;
      setDefaultLayoutSuppressed(true);
      applyWidgetCanvasSnapshot(createSuppressedWidgetCanvasSnapshot(previewSnapshot));
      return;
    }
    suppressedDefaultSnapshotRef.current = null;
    setDefaultLayoutSuppressed(false);
    applyWidgetCanvasSnapshot(previewSnapshot);
  }, [applyWidgetCanvasSnapshot, hydrated, onboardingPending, shouldSuppressWidgetTemplate]);

  useEffect(() => {
    layoutMetaRef.current = layoutMeta;
    savePersistedWidgetLayoutMeta(layoutMeta);
    if (layoutMeta.layoutMode !== 'default') {
      defaultLayoutBaselineKeyRef.current = null;
    }
  }, [layoutMeta]);

  useEffect(() => {
    defaultLayoutSuppressedRef.current = defaultLayoutSuppressed;
  }, [defaultLayoutSuppressed]);

  useEffect(() => {
    if (!onboardingPending || !onboardingUserId) {
      setOnboardingIntroPhase('done');
      return;
    }
    setOnboardingIntroPhase('dock');
    const modalsTimer = window.setTimeout(() => {
      setOnboardingIntroPhase('modals');
    }, 320);
    const doneTimer = window.setTimeout(() => {
      setOnboardingIntroPhase('done');
    }, 860);
    return () => {
      window.clearTimeout(modalsTimer);
      window.clearTimeout(doneTimer);
    };
  }, [onboardingPending, onboardingUserId]);

  useEffect(() => {
    calendarModalRef.current = calendarModal;
    if (calendarModalPersistTimerRef.current !== null) {
      window.clearTimeout(calendarModalPersistTimerRef.current);
    }
    if (defaultLayoutSuppressed && layoutMeta.layoutMode === 'default') return;
    calendarModalPersistTimerRef.current = window.setTimeout(() => {
      savePersistedCalendarModalState(calendarModalRef.current);
    }, 180);
  }, [calendarModal, defaultLayoutSuppressed, layoutMeta.layoutMode]);

  useEffect(() => {
    emailModalRef.current = emailModal;
    if (emailModalPersistTimerRef.current !== null) {
      window.clearTimeout(emailModalPersistTimerRef.current);
    }
    if (defaultLayoutSuppressed && layoutMeta.layoutMode === 'default') return;
    emailModalPersistTimerRef.current = window.setTimeout(() => {
      savePersistedEmailModalState(emailModalRef.current);
    }, 180);
  }, [defaultLayoutSuppressed, emailModal, layoutMeta.layoutMode]);

  useEffect(() => {
    dmModalRef.current = dmModal;
    if (dmModalPersistTimerRef.current !== null) {
      window.clearTimeout(dmModalPersistTimerRef.current);
    }
    if (defaultLayoutSuppressed && layoutMeta.layoutMode === 'default') return;
    dmModalPersistTimerRef.current = window.setTimeout(() => {
      savePersistedDMModalState(dmModalRef.current);
    }, 180);
  }, [defaultLayoutSuppressed, dmModal, layoutMeta.layoutMode]);

  useEffect(() => {
    agentModalRef.current = agentModal;
    if (agentModalPersistTimerRef.current !== null) {
      window.clearTimeout(agentModalPersistTimerRef.current);
    }
    if (defaultLayoutSuppressed && layoutMeta.layoutMode === 'default') return;
    agentModalPersistTimerRef.current = window.setTimeout(() => {
      savePersistedAgentModalState(agentModalRef.current);
    }, 180);
  }, [agentModal, defaultLayoutSuppressed, layoutMeta.layoutMode]);

  useEffect(
    () => () => {
      if (moveRafRef.current !== null) {
        cancelAnimationFrame(moveRafRef.current);
      }
      if (persistTimerRef.current !== null) {
        window.clearTimeout(persistTimerRef.current);
      }
      if (cloudSaveTimerRef.current !== null) {
        window.clearTimeout(cloudSaveTimerRef.current);
      }
      if (calendarModalPersistTimerRef.current !== null) {
        window.clearTimeout(calendarModalPersistTimerRef.current);
      }
      if (emailModalPersistTimerRef.current !== null) {
        window.clearTimeout(emailModalPersistTimerRef.current);
      }
      if (dmModalPersistTimerRef.current !== null) {
        window.clearTimeout(dmModalPersistTimerRef.current);
      }
      if (agentModalPersistTimerRef.current !== null) {
        window.clearTimeout(agentModalPersistTimerRef.current);
      }
      if (defaultLayoutSuppressedRef.current && layoutMetaRef.current.layoutMode === 'default') {
        return;
      }
      savePersistedCalendarModalState(calendarModalRef.current);
      savePersistedEmailModalState(emailModalRef.current);
      savePersistedDMModalState(dmModalRef.current);
      savePersistedAgentModalState(agentModalRef.current);
    },
    [],
  );

  useEffect(() => {
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const bounds = { w: el.clientWidth, h: el.clientHeight };
      setWindows((prev) => clampAllWindows(prev, bounds));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [clampAllWindows]);

  /* ---- persistence hydrate ---- */
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const localLayoutMeta = loadPersistedWidgetLayoutMeta();

      const localSnapshot = loadPersistedWidgetSnapshot();
      const restored = localSnapshot.windows.map(fromPersistedWindow);
      const bounds = getCanvasBounds();
      let clamped = clampAllWindows(restored, bounds);

      const localUsedIds = collectUsedImageIds(clamped);
      if (localUsedIds.length > 0) {
        const imageMap = await loadMemoImageAssets(localUsedIds);
        if (cancelled) return;
        if (Object.keys(imageMap).length > 0) {
          const merged = mergeImageStoreFromMap(clamped, imageMap);
          if (merged !== clamped) {
            clamped = merged;
          }
        }
      }

      if (cancelled) return;
      const localCanvasSnapshot = createWidgetCanvasSnapshot(
        clamped,
        calendarModalRef.current,
        emailModalRef.current,
        dmModalRef.current,
        agentModalRef.current,
      );
      const shouldPreferOnboardingDefault = onboardingPending
        && isEmptyWidgetCanvasSnapshot(localCanvasSnapshot);
      const effectiveLocalLayoutMeta = shouldPreferOnboardingDefault
        ? createDefaultWidgetLayoutMeta()
        : localLayoutMeta;
      const effectiveLocalCanvasSnapshot = shouldPreferOnboardingDefault
        ? createDefaultWidgetCanvasSnapshot()
        : localCanvasSnapshot;

      setLayoutMeta(effectiveLocalLayoutMeta);
      layoutMetaRef.current = effectiveLocalLayoutMeta;
      defaultLayoutBaselineKeyRef.current = effectiveLocalLayoutMeta.layoutMode === 'default'
        ? createWidgetLayoutSignature(effectiveLocalCanvasSnapshot)
        : null;
      cloudUpdatedAtRef.current = Math.max(0, localSnapshot.updatedAt);

      let activeWindows = effectiveLocalCanvasSnapshot.windows;
      if (shouldSuppressWidgetTemplate(effectiveLocalLayoutMeta)) {
        suppressedDefaultSnapshotRef.current = effectiveLocalCanvasSnapshot;
        applyWidgetCanvasSnapshot(createSuppressedWidgetCanvasSnapshot(effectiveLocalCanvasSnapshot));
        setDefaultLayoutSuppressed(true);
      } else {
        suppressedDefaultSnapshotRef.current = null;
        applyWidgetCanvasSnapshot(effectiveLocalCanvasSnapshot);
        setDefaultLayoutSuppressed(false);
      }

      /* Phase 11-5: when `mode.widget.cloudSync` is disabled the
       * canvas runs purely local. Tenants that ban personal-data
       * upload (e.g. on-prem Samsung) can flip this off without
       * affecting the rest of the widget UX. */
      if (!cloudSyncEnabled) return;

      try {
        const remote = await fetchWidgetCanvasFromCloud();
        if (cancelled) return;

        const remoteUpdatedAt = Number(remote.updated_at || 0);
        const parsedRemote = parseRemotePayload(
          remote.payload,
          calendarModalRef.current,
          emailModalRef.current,
          dmModalRef.current,
          agentModalRef.current,
        );
        const shouldSeedDefaultFromEmptyRemote = !parsedRemote
          && remoteUpdatedAt <= 0
          && isEmptyWidgetCanvasSnapshot(localCanvasSnapshot);

        if (shouldSeedDefaultFromEmptyRemote) {
          const seededSnapshot = createDefaultWidgetCanvasSnapshot();
          const seededLayoutMeta = createDefaultWidgetLayoutMeta();
          const seededUpdatedAt = Math.max(Date.now(), Math.max(0, localSnapshot.updatedAt) + 1);

          setLayoutMeta(seededLayoutMeta);
          layoutMetaRef.current = seededLayoutMeta;
          defaultLayoutBaselineKeyRef.current = createWidgetLayoutSignature(seededSnapshot);
          cloudUpdatedAtRef.current = seededUpdatedAt;

          savePersistedWidgetWindows(seededSnapshot.windows.map(toPersistedWindow), seededUpdatedAt);
          savePersistedCalendarModalState(seededSnapshot.calendarModal);
          savePersistedEmailModalState(seededSnapshot.emailModal);
          savePersistedDMModalState(seededSnapshot.dmModal);
          savePersistedAgentModalState(seededSnapshot.agentModal);
          savePersistedWidgetLayoutMeta(seededLayoutMeta);

          if (shouldSuppressWidgetTemplate(seededLayoutMeta)) {
            suppressedDefaultSnapshotRef.current = seededSnapshot;
            applyWidgetCanvasSnapshot(createSuppressedWidgetCanvasSnapshot(seededSnapshot));
            setDefaultLayoutSuppressed(true);
          } else {
            suppressedDefaultSnapshotRef.current = null;
            applyWidgetCanvasSnapshot(seededSnapshot);
            setDefaultLayoutSuppressed(false);
          }
          activeWindows = seededSnapshot.windows;

          void saveWidgetCanvasToCloud(
            toRemotePayload(
              seededSnapshot.windows,
              seededSnapshot.calendarModal,
              seededSnapshot.emailModal,
              seededSnapshot.dmModal,
              seededSnapshot.agentModal,
              seededLayoutMeta,
            ),
            seededUpdatedAt,
          ).catch(() => {
            // 최초 기본 레이아웃 백필은 best-effort
          });
        } else if (parsedRemote) {
          const remoteWindows = clampAllWindows(parsedRemote.windows, bounds);
          const remoteCalendarModal = parsedRemote.calendarModal;
          const remoteEmailModal = parsedRemote.emailModal;
          const remoteDMModal = parsedRemote.dmModal;
          const remoteAgentModal = parsedRemote.agentModal;
          const remoteLayoutMeta = parsedRemote.layoutMeta;
          const remoteCanvasSnapshotRaw = createWidgetCanvasSnapshot(
            remoteWindows,
            remoteCalendarModal,
            remoteEmailModal,
            remoteDMModal,
            remoteAgentModal,
          );
          const shouldRepairBrokenDefaultRemote = remoteLayoutMeta.layoutMode === 'default'
            && isEmptyWidgetCanvasSnapshot(remoteCanvasSnapshotRaw);
          const remoteCanvasSnapshot = shouldRepairBrokenDefaultRemote
            ? createDefaultWidgetCanvasSnapshot()
            : remoteCanvasSnapshotRaw;
          const effectiveRemoteLayoutMeta = shouldRepairBrokenDefaultRemote
            ? createDefaultWidgetLayoutMeta()
            : remoteLayoutMeta;
          const effectiveRemoteUpdatedAt = shouldRepairBrokenDefaultRemote
            ? Math.max(remoteUpdatedAt, Date.now())
            : remoteUpdatedAt;
          const shouldApplyRemote = effectiveRemoteUpdatedAt > Math.max(0, localSnapshot.updatedAt)
            || (
              Math.max(0, localSnapshot.updatedAt) === 0
              && (
                effectiveRemoteLayoutMeta.layoutMode === 'default'
                || !isEmptyWidgetCanvasSnapshot(remoteCanvasSnapshot)
              )
            );

          if (!shouldApplyRemote) {
            // 이미 더 최신인 로컬 스냅샷이 있으면 원격으로 덮어쓰지 않음
          } else {
            setLayoutMeta(effectiveRemoteLayoutMeta);
            layoutMetaRef.current = effectiveRemoteLayoutMeta;
            defaultLayoutBaselineKeyRef.current = effectiveRemoteLayoutMeta.layoutMode === 'default'
              ? createWidgetLayoutSignature(remoteCanvasSnapshot)
              : null;
            cloudUpdatedAtRef.current = effectiveRemoteUpdatedAt;

            savePersistedWidgetWindows(remoteCanvasSnapshot.windows.map(toPersistedWindow), effectiveRemoteUpdatedAt);
            savePersistedCalendarModalState(remoteCanvasSnapshot.calendarModal);
            savePersistedEmailModalState(remoteCanvasSnapshot.emailModal);
            savePersistedDMModalState(remoteCanvasSnapshot.dmModal);
            savePersistedAgentModalState(remoteCanvasSnapshot.agentModal);
            savePersistedWidgetLayoutMeta(effectiveRemoteLayoutMeta);

            if (shouldSuppressWidgetTemplate(effectiveRemoteLayoutMeta)) {
              suppressedDefaultSnapshotRef.current = remoteCanvasSnapshot;
              applyWidgetCanvasSnapshot(createSuppressedWidgetCanvasSnapshot(remoteCanvasSnapshot));
              setDefaultLayoutSuppressed(true);
            } else {
              suppressedDefaultSnapshotRef.current = null;
              applyWidgetCanvasSnapshot(remoteCanvasSnapshot);
              setDefaultLayoutSuppressed(false);
            }
            activeWindows = remoteCanvasSnapshot.windows;

            if (shouldRepairBrokenDefaultRemote) {
              void saveWidgetCanvasToCloud(
                toRemotePayload(
                  remoteCanvasSnapshot.windows,
                  remoteCanvasSnapshot.calendarModal,
                  remoteCanvasSnapshot.emailModal,
                  remoteCanvasSnapshot.dmModal,
                  remoteCanvasSnapshot.agentModal,
                  effectiveRemoteLayoutMeta,
                ),
                effectiveRemoteUpdatedAt,
              ).catch(() => {
                // 손상된 기본 레이아웃 복구는 best-effort
              });
            }

            const upserts: Promise<void>[] = [];
            for (const window of remoteCanvasSnapshot.windows) {
              for (const [imageId, dataUrl] of Object.entries(window.memo.imageStore)) {
                if (!dataUrl.startsWith('data:image/')) continue;
                upserts.push(upsertMemoImageAsset(imageId, dataUrl));
              }
            }
            if (upserts.length > 0) {
              void Promise.allSettled(upserts);
            }
          }
        }
      } catch {
        // 네트워크 불가 시 로컬 상태를 그대로 사용
      }

      void pruneOrphanMemoImageAssets(new Set(collectUsedImageIds(activeWindows)));
      if (!cancelled) setHydrated(true);
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [
    applyWidgetCanvasSnapshot,
    clampAllWindows,
    getCanvasBounds,
    onboardingPending,
    shouldSuppressWidgetTemplate,
  ]);

  /* ---- persistence save (debounced) ---- */
  useEffect(() => {
    if (!hydrated) return;
    if (defaultLayoutSuppressed && layoutMeta.layoutMode === 'default') return;

    const currentSnapshot = captureWidgetCanvasSnapshot();
    const currentSignature = createWidgetLayoutSignature(currentSnapshot);
    if (
      layoutMeta.layoutMode === 'default'
      && defaultLayoutBaselineKeyRef.current
      && currentSignature !== defaultLayoutBaselineKeyRef.current
    ) {
      setLayoutMeta((prev) => (
        prev.layoutMode === 'custom'
          ? prev
          : { ...prev, layoutMode: 'custom' }
      ));
      return;
    }

    if (persistTimerRef.current !== null) {
      window.clearTimeout(persistTimerRef.current);
    }
    persistTimerRef.current = window.setTimeout(() => {
      const snapshot = windowsRef.current;
      const calendarSnapshot = calendarModalRef.current;
      const emailSnapshot = emailModalRef.current;
      const dmSnapshot = dmModalRef.current;
      const agentSnapshot = agentModalRef.current;
      const nextUpdatedAt = Math.max(Date.now(), cloudUpdatedAtRef.current + 1);
      savePersistedWidgetWindows(snapshot.map(toPersistedWindow), nextUpdatedAt);
      savePersistedCalendarModalState(calendarSnapshot);
      savePersistedEmailModalState(emailSnapshot);
      savePersistedDMModalState(dmSnapshot);
      savePersistedAgentModalState(agentSnapshot);
      savePersistedWidgetLayoutMeta(layoutMetaRef.current);
      cloudUpdatedAtRef.current = nextUpdatedAt;

      const now = Date.now();
      if (now - lastPruneAtRef.current > 15_000) {
        lastPruneAtRef.current = now;
        void pruneOrphanMemoImageAssets(new Set(collectUsedImageIds(snapshot)));
      }

      if (cloudSaveTimerRef.current !== null) {
        window.clearTimeout(cloudSaveTimerRef.current);
      }
      /* Phase 11-5: skip the cloud save scheduler entirely when the
       * tenant has disabled `mode.widget.cloudSync`.  Local persist
       * (timer block above) still runs so the canvas survives reloads
       * within the same browser. */
      if (!cloudSyncEnabled) return;
      cloudSaveTimerRef.current = window.setTimeout(() => {
        const cloudPayload = toRemotePayload(
          windowsRef.current,
          calendarModalRef.current,
          emailModalRef.current,
          dmModalRef.current,
          agentModalRef.current,
          layoutMetaRef.current,
        );
        const cloudUpdatedAt = Math.max(Date.now(), cloudUpdatedAtRef.current + 1);
        void saveWidgetCanvasToCloud(cloudPayload, cloudUpdatedAt)
          .then((saved) => {
            cloudUpdatedAtRef.current = Math.max(cloudUpdatedAt, Number(saved.updated_at || 0));
          })
          .catch(() => {
            // 네트워크 오류는 다음 저장 사이클에서 자동 재시도
          });
      }, 900);
    }, 300);

    return () => {
      if (persistTimerRef.current !== null) {
        window.clearTimeout(persistTimerRef.current);
      }
      if (cloudSaveTimerRef.current !== null) {
        window.clearTimeout(cloudSaveTimerRef.current);
      }
    };
  }, [
    agentModal,
    calendarModal,
    captureWidgetCanvasSnapshot,
    defaultLayoutSuppressed,
    dmModal,
    emailModal,
    hydrated,
    layoutMeta.layoutMode,
    windows,
  ]);

  useEffect(() => {
    if (!hydrated) return;
    const flushNow = () => {
      if (defaultLayoutSuppressedRef.current && layoutMetaRef.current.layoutMode === 'default') {
        return;
      }
      const snapshot = windowsRef.current;
      const currentCanvasSnapshot = captureWidgetCanvasSnapshot();
      const currentSignature = createWidgetLayoutSignature(currentCanvasSnapshot);
      const nextLayoutMeta = (
        layoutMetaRef.current.layoutMode === 'default'
        && defaultLayoutBaselineKeyRef.current
        && currentSignature !== defaultLayoutBaselineKeyRef.current
      )
        ? { ...layoutMetaRef.current, layoutMode: 'custom' as const }
        : layoutMetaRef.current;
      savePersistedCalendarModalState(calendarModalRef.current);
      savePersistedEmailModalState(emailModalRef.current);
      savePersistedDMModalState(dmModalRef.current);
      savePersistedAgentModalState(agentModalRef.current);
      savePersistedWidgetLayoutMeta(nextLayoutMeta);
      const nextUpdatedAt = Math.max(Date.now(), cloudUpdatedAtRef.current + 1);
      savePersistedWidgetWindows(snapshot.map(toPersistedWindow), nextUpdatedAt);
      cloudUpdatedAtRef.current = nextUpdatedAt;
      if (!cloudSyncEnabled) return;
      void saveWidgetCanvasToCloud(
        toRemotePayload(
          snapshot,
          calendarModalRef.current,
          emailModalRef.current,
          dmModalRef.current,
          agentModalRef.current,
          nextLayoutMeta,
        ),
        nextUpdatedAt,
      ).catch(() => {
        // 페이지 종료 시점의 클라우드 저장은 best-effort
      });
    };
    window.addEventListener('beforeunload', flushNow);
    return () => window.removeEventListener('beforeunload', flushNow);
  }, [captureWidgetCanvasSnapshot, hydrated, cloudSyncEnabled]);

  useEffect(() => {
    if (!hydrated) return;
    if (layoutMeta.layoutMode !== 'default') {
      if (defaultLayoutSuppressed) {
        suppressedDefaultSnapshotRef.current = null;
        setDefaultLayoutSuppressed(false);
      }
      return;
    }

    if (shouldSuppressWidgetTemplate(layoutMeta)) {
      if (defaultLayoutSuppressed) return;
      const snapshot = captureWidgetCanvasSnapshot();
      suppressedDefaultSnapshotRef.current = snapshot;
      defaultLayoutBaselineKeyRef.current = createWidgetLayoutSignature(snapshot);
      applyWidgetCanvasSnapshot(createSuppressedWidgetCanvasSnapshot(snapshot));
      setDefaultLayoutSuppressed(true);
      return;
    }

    if (!defaultLayoutSuppressed) return;
    const snapshot = suppressedDefaultSnapshotRef.current;
    if (!snapshot) {
      setDefaultLayoutSuppressed(false);
      return;
    }
    applyWidgetCanvasSnapshot(snapshot);
    defaultLayoutBaselineKeyRef.current = createWidgetLayoutSignature(snapshot);
    suppressedDefaultSnapshotRef.current = null;
    setDefaultLayoutSuppressed(false);
  }, [
    applyWidgetCanvasSnapshot,
    captureWidgetCanvasSnapshot,
    defaultLayoutSuppressed,
    hydrated,
    layoutMeta,
    shouldSuppressWidgetTemplate,
  ]);

  const bringToFront = useCallback((id: string) => {
    setWindows((prev) => {
      const target = prev.find((window) => window.id === id);
      if (!target) return prev;
      const currentTop = Math.max(
        10,
        ...Object.values(modalZOrderRef.current),
        ...prev.map((window) => window.zIndex),
      );
      topZ.current = currentTop;
      if (target.zIndex === currentTop) return prev;
      const nextZ = currentTop + 1;
      topZ.current = nextZ;
      return prev.map((window) => (
        window.id === id ? { ...window, zIndex: nextZ } : window
      ));
    });
  }, []);

  const bringModalToFront = useCallback((type: WidgetModalType) => {
    setModalZOrder((prev) => {
      const currentTop = Math.max(
        10,
        ...Object.values(prev),
        ...windowsRef.current.map((window) => window.zIndex),
      );
      topZ.current = currentTop;
      if (prev[type] === currentTop) {
        modalZOrderRef.current = prev;
        return prev;
      }
      const nextZ = currentTop + 1;
      topZ.current = nextZ;
      const next = { ...prev, [type]: nextZ };
      modalZOrderRef.current = next;
      return next;
    });
  }, []);

  const addMemo = useCallback(() => {
    const id = `memo-${++globalCounter}-${Date.now()}`;
    const bounds = getCanvasBounds();
    const pos = nextMemoPosition(windows, bounds);
    const isMobile = bounds.w <= 768;
    const w = isMobile
      ? Math.min(Math.max(220, bounds.w - 24), MEMO_DEFAULT_W)
      : MEMO_DEFAULT_W;
    const h = isMobile
      ? Math.min(Math.max(240, bounds.h - 24), MEMO_DEFAULT_H)
      : MEMO_DEFAULT_H;
    const rect = clampRectToBounds({ x: pos.x, y: pos.y, w, h }, bounds);
    topZ.current += 1;
    const win: FloatingWindow = {
      id,
      type: 'memo',
      rect,
      pinned: false,
      opacity: 1,
      zIndex: topZ.current,
      memo: { id, content: '', imageStore: {} },
    };
    setWindows((prev) => [...prev, win]);
  }, [windows, getCanvasBounds, clampRectToBounds]);

  const moveWindow = useCallback(
    (id: string, x: number, y: number) => {
      pendingMovesRef.current.set(id, { x, y });
      if (moveRafRef.current === null) {
        moveRafRef.current = requestAnimationFrame(flushPendingMoves);
      }
    },
    [flushPendingMoves],
  );

  const resizeWindow = useCallback(
    (id: string, partial: Partial<FloatingRect>) => {
      const bounds = getCanvasBounds();
      setWindows((prev) =>
        prev.map((w) => {
          if (w.id !== id) return w;
          const nextRect = clampRectToBounds({ ...w.rect, ...partial }, bounds);
          return { ...w, rect: nextRect };
        }),
      );
    },
    [getCanvasBounds, clampRectToBounds],
  );

  const togglePin = useCallback((id: string) => {
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, pinned: !w.pinned } : w)),
    );
  }, []);

  const updateWindowOpacity = useCallback((id: string, opacity: number) => {
    const normalized = Math.min(1, Math.max(0.35, opacity));
    setWindows((prev) =>
      prev.map((w) => (w.id === id ? { ...w, opacity: normalized } : w)),
    );
  }, []);

  const deleteWindow = useCallback((id: string) => {
    const target = windowsRef.current.find((w) => w.id === id);
    if (target?.type === 'memo') {
      const ids = extractMemoImageIds(target.memo.content);
      if (ids.length > 0) {
        void deleteMemoImageAssets(ids);
      }
    }
    setWindows((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const updateMemoContent = useCallback((id: string, content: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id ? { ...w, memo: { ...w.memo, content } } : w,
      ),
    );
  }, []);

  const addMemoImage = useCallback((id: string, imgId: string, dataUrl: string) => {
    setWindows((prev) =>
      prev.map((w) =>
        w.id === id
          ? { ...w, memo: { ...w.memo, imageStore: { ...w.memo.imageStore, [imgId]: dataUrl } } }
          : w,
      ),
    );
    void upsertMemoImageAsset(imgId, dataUrl);
  }, []);

  const startCanvasPan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!panMode || !hasOverflowX || e.target !== e.currentTarget || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    panDragRef.current = {
      startX: e.clientX,
      basePanX: panX,
    };
    setIsPanning(true);
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [hasOverflowX, panMode, panX]);

  /* ---- Dock handlers ---- */
  const handleMouseLeave = useCallback(() => {
    setHoveredIdx(null);
  }, []);

  const handleClick = useCallback(
    (item: DockItemConfig) => {
      setBouncingId(item.id);
      setTimeout(() => setBouncingId(null), 500);

      switch (item.id) {
        case 'agent':
          releaseSuppressedDefaultLayoutForInteraction();
          bringModalToFront('agent');
          setAgentModal((prev) => ({ ...prev, open: true }));
          ensureStageRectVisible(modalRectToStageRect(agentModal.rect));
          break;
        case 'chat':
          releaseSuppressedDefaultLayoutForInteraction();
          bringModalToFront('dm');
          setDmModal((prev) => ({ ...prev, open: true }));
          ensureStageRectVisible(modalRectToStageRect(dmModal.rect));
          break;
        case 'calendar':
          releaseSuppressedDefaultLayoutForInteraction();
          bringModalToFront('calendar');
          setCalendarModal((prev) => ({ ...prev, open: true }));
          ensureStageRectVisible(modalRectToStageRect(calendarModal.rect));
          break;
        case 'mail':
          releaseSuppressedDefaultLayoutForInteraction();
          bringModalToFront('email');
          setEmailModal((prev) => ({ ...prev, open: true }));
          ensureStageRectVisible(modalRectToStageRect(emailModal.rect));
          break;
        case 'memo':
          releaseSuppressedDefaultLayoutForInteraction();
          addMemo();
          break;
        case 'pan':
          if (!hasOverflowX) break;
          setPanMode((prev) => !prev);
          break;
        default:
          break;
      }
    },
    [
      addMemo,
      agentModal.rect,
      bringModalToFront,
      calendarModal.rect,
      dmModal.rect,
      emailModal.rect,
      ensureStageRectVisible,
      hasOverflowX,
      modalRectToStageRect,
      releaseSuppressedDefaultLayoutForInteraction,
    ],
  );

  const handleMailSelect = useCallback(
    (mailFiles: FileAttachment[]) => {
      setEmailModal((prev) => ({ ...prev, open: false }));
      if (mailFiles.length === 0) return;
      openWorkspaceMode('agent');
      setTimeout(() => {
        emitAppEvent('chat:addMailAttachments', { files: mailFiles });
      }, 300);
    },
    [openWorkspaceMode],
  );

  const updateCalendarModalRect = useCallback((partial: Partial<PersistedCalendarModalRect>) => {
    setCalendarModal((prev) => {
      const nextRect: PersistedCalendarModalRect = {
        x: Number.isFinite(partial.x) ? Number(partial.x) : prev.rect.x,
        y: Number.isFinite(partial.y) ? Number(partial.y) : prev.rect.y,
        w: Number.isFinite(partial.w)
          ? Math.max(CALENDAR_MODAL_MIN_W, Number(partial.w))
          : prev.rect.w,
        h: Number.isFinite(partial.h)
          ? Math.max(CALENDAR_MODAL_MIN_H, Number(partial.h))
          : prev.rect.h,
      };
      if (
        nextRect.x === prev.rect.x
        && nextRect.y === prev.rect.y
        && nextRect.w === prev.rect.w
        && nextRect.h === prev.rect.h
      ) {
        return prev;
      }
      return { ...prev, rect: nextRect };
    });
  }, []);

  const updateCalendarModalPinned = useCallback((pinned: boolean) => {
    setCalendarModal((prev) => (prev.pinned === pinned ? prev : { ...prev, pinned }));
  }, []);

  const updateCalendarModalOpacity = useCallback((opacity: number) => {
    const normalized = Math.min(1, Math.max(0.35, opacity));
    setCalendarModal((prev) => (
      prev.opacity === normalized ? prev : { ...prev, opacity: normalized }
    ));
  }, []);

  const updateCalendarModalSelectedDate = useCallback((selectedDate: string) => {
    setCalendarModal((prev) => (
      prev.selectedDate === selectedDate ? prev : { ...prev, selectedDate }
    ));
  }, []);

  const updateCalendarModalSelectedProviders = useCallback((selectedProviders: CalendarProviderId[]) => {
    setCalendarModal((prev) => {
      const deduped = selectedProviders.filter((item, index) => selectedProviders.indexOf(item) === index);
      if (
        deduped.length === prev.selectedProviders.length
        && deduped.every((item, index) => item === prev.selectedProviders[index])
      ) {
        return prev;
      }
      return { ...prev, selectedProviders: deduped };
    });
  }, []);

  const updateEmailModalRect = useCallback((partial: Partial<PersistedEmailModalRect>) => {
    setEmailModal((prev) => {
      const nextRect: PersistedEmailModalRect = {
        x: Number.isFinite(partial.x) ? Number(partial.x) : prev.rect.x,
        y: Number.isFinite(partial.y) ? Number(partial.y) : prev.rect.y,
        w: Number.isFinite(partial.w)
          ? Math.max(EMAIL_MODAL_MIN_W, Number(partial.w))
          : prev.rect.w,
        h: Number.isFinite(partial.h)
          ? Math.max(EMAIL_MODAL_MIN_H, Number(partial.h))
          : prev.rect.h,
      };
      if (
        nextRect.x === prev.rect.x
        && nextRect.y === prev.rect.y
        && nextRect.w === prev.rect.w
        && nextRect.h === prev.rect.h
      ) {
        return prev;
      }
      return { ...prev, rect: nextRect };
    });
  }, []);

  const updateEmailModalPinned = useCallback((pinned: boolean) => {
    setEmailModal((prev) => (prev.pinned === pinned ? prev : { ...prev, pinned }));
  }, []);

  const updateEmailModalOpacity = useCallback((opacity: number) => {
    const normalized = Math.min(1, Math.max(0.35, opacity));
    setEmailModal((prev) => (
      prev.opacity === normalized ? prev : { ...prev, opacity: normalized }
    ));
  }, []);

  const updateEmailModalTab = useCallback((tab: MailProvider) => {
    setEmailModal((prev) => (prev.initialTab === tab ? prev : { ...prev, initialTab: tab }));
  }, []);

  const updateDMModalRect = useCallback((partial: Partial<PersistedDMModalRect>) => {
    setDmModal((prev) => {
      const nextRect: PersistedDMModalRect = {
        x: Number.isFinite(partial.x) ? Number(partial.x) : prev.rect.x,
        y: Number.isFinite(partial.y) ? Number(partial.y) : prev.rect.y,
        w: Number.isFinite(partial.w)
          ? Math.max(DM_MODAL_MIN_W, Number(partial.w))
          : prev.rect.w,
        h: Number.isFinite(partial.h)
          ? Math.max(DM_MODAL_MIN_H, Number(partial.h))
          : prev.rect.h,
      };
      if (
        nextRect.x === prev.rect.x
        && nextRect.y === prev.rect.y
        && nextRect.w === prev.rect.w
        && nextRect.h === prev.rect.h
      ) {
        return prev;
      }
      return { ...prev, rect: nextRect };
    });
  }, []);

  const updateDMModalPinned = useCallback((pinned: boolean) => {
    setDmModal((prev) => (prev.pinned === pinned ? prev : { ...prev, pinned }));
  }, []);

  const updateDMModalOpacity = useCallback((opacity: number) => {
    const normalized = Math.min(1, Math.max(0.35, opacity));
    setDmModal((prev) => (
      prev.opacity === normalized ? prev : { ...prev, opacity: normalized }
    ));
  }, []);

  const updateAgentModalRect = useCallback((partial: Partial<PersistedAgentModalRect>) => {
    setAgentModal((prev) => {
      const nextRect: PersistedAgentModalRect = {
        x: Number.isFinite(partial.x) ? Number(partial.x) : prev.rect.x,
        y: Number.isFinite(partial.y) ? Number(partial.y) : prev.rect.y,
        w: Number.isFinite(partial.w)
          ? Math.max(AGENT_MODAL_MIN_W, Number(partial.w))
          : prev.rect.w,
        h: Number.isFinite(partial.h)
          ? Math.max(AGENT_MODAL_MIN_H, Number(partial.h))
          : prev.rect.h,
      };
      if (
        nextRect.x === prev.rect.x
        && nextRect.y === prev.rect.y
        && nextRect.w === prev.rect.w
        && nextRect.h === prev.rect.h
      ) {
        return prev;
      }
      return { ...prev, rect: nextRect };
    });
  }, []);

  const updateAgentModalPinned = useCallback((pinned: boolean) => {
    setAgentModal((prev) => (prev.pinned === pinned ? prev : { ...prev, pinned }));
  }, []);

  const updateAgentModalOpacity = useCallback((opacity: number) => {
    const normalized = Math.min(1, Math.max(0.35, opacity));
    setAgentModal((prev) => (
      prev.opacity === normalized ? prev : { ...prev, opacity: normalized }
    ));
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    if (calendarModal.open) {
      ensureStageRectVisible(modalRectToStageRect(calendarModal.rect));
      return;
    }
    if (emailModal.open) {
      ensureStageRectVisible(modalRectToStageRect(emailModal.rect));
      return;
    }
    if (dmModal.open) {
      ensureStageRectVisible(modalRectToStageRect(dmModal.rect));
      return;
    }
    if (agentModal.open) {
      ensureStageRectVisible(modalRectToStageRect(agentModal.rect));
    }
  }, [
    agentModal.open,
    agentModal.rect,
    calendarModal.open,
    calendarModal.rect,
    dmModal.open,
    dmModal.rect,
    emailModal.open,
    emailModal.rect,
    ensureStageRectVisible,
    hydrated,
    modalRectToStageRect,
  ]);

  const getNeighborDir = useCallback(
    (idx: number): -1 | 0 | 1 => {
      if (hoveredIdx === null) return 0;
      if (idx === hoveredIdx - 1) return 1;
      if (idx === hoveredIdx + 1) return -1;
      return 0;
    },
    [hoveredIdx],
  );

  const iconInner = Math.round(ICON_SIZE * 0.52);
  const deferOnboardingModalMount = onboardingPending && onboardingIntroPhase === 'dock';
  const hasVisibleWidgets = (
    windows.length > 0
    || calendarModal.open
    || emailModal.open
    || dmModal.open
    || agentModal.open
  );
  const showWidgetStartOverlay = hydrated && !onboardingPending && !hasVisibleWidgets;

  return (
    <Canvas ref={viewportRef}>
      <CanvasStage ref={canvasRef} $width={stageWidth} $panX={panX} $zIndex={1}>
        {/* Floating windows layer */}
        <FloatingLayer
          $panReady={panMode && hasOverflowX}
          $panning={isPanning}
          onPointerDown={startCanvasPan}
        >
          {windows.map((w) => {
            if (w.type === 'memo') {
              return (
                <MemoWindow
                  key={w.id}
                  memo={w.memo}
                  rect={w.rect}
                  pinned={w.pinned}
                  opacity={w.opacity}
                  zIndex={w.zIndex}
                  onMove={moveWindow}
                  onResize={resizeWindow}
                  onFocus={bringToFront}
                  onTogglePin={togglePin}
                  onOpacityChange={updateWindowOpacity}
                  onDelete={deleteWindow}
                  onContentChange={updateMemoContent}
                  onImageAdd={addMemoImage}
                />
              );
            }
            return null;
          })}
        </FloatingLayer>
        {!deferOnboardingModalMount && calendarModal.open && (
          <Suspense fallback={null}>
            <CalendarWidgetModal
              open
              onClose={() => setCalendarModal((prev) => ({ ...prev, open: false }))}
              selectedDate={calendarModal.selectedDate}
              selectedProviders={calendarModal.selectedProviders}
              onSelectedDateChange={updateCalendarModalSelectedDate}
              onSelectedProvidersChange={updateCalendarModalSelectedProviders}
              modalDraggable
              modalPinnable
              modalDefaultPinned={calendarModal.pinned}
              modalInitialOffsetX={calendarModal.rect.x}
              modalInitialOffsetY={calendarModal.rect.y}
              modalOpacityAdjustable
              modalDefaultOpacity={calendarModal.opacity}
              modalDefaultWidth={calendarModal.rect.w}
              modalDefaultHeight={calendarModal.rect.h}
              modalMinWidth={CALENDAR_MODAL_MIN_W}
              modalMinHeight={CALENDAR_MODAL_MIN_H}
              modalCloseOnBackdrop={false}
              modalDimBackdrop={false}
              modalPassthroughBackdrop
              mountMode="parent"
              hostBounds={modalHostBounds}
              modalOnSizeChange={({ w, h }) => updateCalendarModalRect({ w, h })}
              modalOnOffsetChange={({ x, y }) => updateCalendarModalRect({ x, y })}
              modalOnPinnedChange={updateCalendarModalPinned}
              modalOnOpacityChange={updateCalendarModalOpacity}
              modalLayerZIndex={modalZOrder.calendar}
              modalOnActivate={() => bringModalToFront('calendar')}
            />
          </Suspense>
        )}
        {!deferOnboardingModalMount && agentModal.open && (
          <Suspense fallback={null}>
            <AgentWidgetModal
              open
              onClose={() => setAgentModal((prev) => ({ ...prev, open: false }))}
              modalDraggable
              modalPinnable
              modalDefaultPinned={agentModal.pinned}
              modalInitialOffsetX={agentModal.rect.x}
              modalInitialOffsetY={agentModal.rect.y}
              modalOpacityAdjustable
              modalDefaultOpacity={agentModal.opacity}
              modalDefaultWidth={agentModal.rect.w}
              modalDefaultHeight={agentModal.rect.h}
              modalMinWidth={AGENT_MODAL_MIN_W}
              modalMinHeight={AGENT_MODAL_MIN_H}
              modalCloseOnBackdrop={false}
              modalDimBackdrop={false}
              modalPassthroughBackdrop
              mountMode="parent"
              hostBounds={modalHostBounds}
              modalOnSizeChange={({ w, h }) => updateAgentModalRect({ w, h })}
              modalOnOffsetChange={({ x, y }) => updateAgentModalRect({ x, y })}
              modalOnPinnedChange={updateAgentModalPinned}
              modalOnOpacityChange={updateAgentModalOpacity}
              modalLayerZIndex={modalZOrder.agent}
              modalOnActivate={() => bringModalToFront('agent')}
            />
          </Suspense>
        )}

        {!deferOnboardingModalMount && dmModal.open && (
          <Suspense fallback={null}>
            <DMWidgetModal
              open
              onClose={() => setDmModal((prev) => ({ ...prev, open: false }))}
              modalDraggable
              modalPinnable
              modalDefaultPinned={dmModal.pinned}
              modalInitialOffsetX={dmModal.rect.x}
              modalInitialOffsetY={dmModal.rect.y}
              modalOpacityAdjustable
              modalDefaultOpacity={dmModal.opacity}
              modalDefaultWidth={dmModal.rect.w}
              modalDefaultHeight={dmModal.rect.h}
              modalMinWidth={DM_MODAL_MIN_W}
              modalCloseOnBackdrop={false}
              modalDimBackdrop={false}
              modalPassthroughBackdrop
              mountMode="parent"
              hostBounds={modalHostBounds}
              modalOnSizeChange={({ w, h }) => updateDMModalRect({ w, h })}
              modalOnOffsetChange={({ x, y }) => updateDMModalRect({ x, y })}
              modalOnPinnedChange={updateDMModalPinned}
              modalOnOpacityChange={updateDMModalOpacity}
              modalLayerZIndex={modalZOrder.dm}
              modalOnActivate={() => bringModalToFront('dm')}
            />
          </Suspense>
        )}

        {!deferOnboardingModalMount && emailModal.open && (
          <Suspense fallback={null}>
            <EmailModal
              open
              onClose={() => setEmailModal((prev) => ({ ...prev, open: false }))}
              initialTab={emailModal.initialTab}
              onSelect={handleMailSelect}
              onTabChange={updateEmailModalTab}
              showUploadActions={false}
              modalDraggable
              modalPinnable
              modalDefaultPinned={emailModal.pinned}
              modalInitialOffsetX={emailModal.rect.x}
              modalInitialOffsetY={emailModal.rect.y}
              modalOpacityAdjustable
              modalDefaultOpacity={emailModal.opacity}
              modalDefaultWidth={emailModal.rect.w}
              modalDefaultHeight={emailModal.rect.h}
              modalMinWidth={320}
              modalCloseOnBackdrop={false}
              modalDimBackdrop={false}
              modalPassthroughBackdrop
              mountMode="parent"
              hostBounds={modalHostBounds}
              modalOnSizeChange={({ w, h }) => updateEmailModalRect({ w, h })}
              modalOnOffsetChange={({ x, y }) => updateEmailModalRect({ x, y })}
              modalOnPinnedChange={updateEmailModalPinned}
              modalOnOpacityChange={updateEmailModalOpacity}
              modalLayerZIndex={modalZOrder.email}
              modalOnActivate={() => bringModalToFront('email')}
            />
          </Suspense>
        )}
      </CanvasStage>

      {showWidgetStartOverlay && (
        <WidgetStartOverlay
          titlePrefix="Askyour"
          rotatingTitles={AGENT_BRAND_SUFFIXES}
          titleSize="1.75rem"
          description={t('dock.startDescription')}
          descriptionTone="strong"
        />
      )}

      {/* Dock */}
      <DockWrapper>
        <DockBar
          onMouseLeave={handleMouseLeave}
        >
          {visibleDockItems.map((item, idx) => {
            const isHovered = hoveredIdx === idx;
            const neighborDir = getNeighborDir(idx);
            const isActive = item.id === 'pan' && panMode;
            const isDisabled = item.id === 'pan' && !hasOverflowX;
            return (
              <React.Fragment key={item.id}>
                {item.id === 'pan' && <DockDivider />}
                <ItemOuter
                  $bouncing={bouncingId === item.id}
                  $hovered={isHovered}
                  $neighborDir={neighborDir}
                  $disabled={isDisabled}
                  onMouseEnter={() => !isDisabled && setHoveredIdx(idx)}
                  onMouseLeave={() => !isDisabled && setHoveredIdx(null)}
                  onClick={() => !isDisabled && handleClick(item)}
                >
                  <TooltipBubble $visible={isHovered}>
                    {t(item.label)}
                  </TooltipBubble>
                  <ItemVisual
                    $bouncing={bouncingId === item.id}
                    $hovered={isHovered}
                    $neighborDir={neighborDir}
                    $disabled={isDisabled}
                  >
                    <IconSquare $hovered={isHovered} $active={isActive} $disabled={isDisabled}>
                      {item.iconSrc ? (
                        <IconImg
                          src={item.iconSrc}
                          alt={t(item.label)}
                          draggable={false}
                          $contain={item.iconContain}
                        />
                      ) : item.icon ? (
                        <item.icon
                          size={iconInner}
                          strokeWidth={1.7}
                          absoluteStrokeWidth
                        />
                      ) : null}
                    </IconSquare>
                  </ItemVisual>
                </ItemOuter>
              </React.Fragment>
            );
          })}
        </DockBar>
        <DockReflection />
      </DockWrapper>

      {panMode && hasOverflowX && (
        <PanOverlay
          $panning={isPanning}
          onPointerDown={startCanvasPan}
        />
      )}
    </Canvas>
  );
};

export default WidgetPage;
